<template>
  <v-card class="mx-auto mt-5" outlined tile>
    <v-toolbar flat>
      <v-spacer />
      <v-btn text color="primary" @click="add">
        <v-icon>add</v-icon>
        Add
      </v-btn>
    </v-toolbar>

    <v-data-table
      dense
      :headers="this.headers"
      :options.sync="pagination"
      :server-items-length="this.list.count"
      :items="this.list.items"
      :loading="$apollo.queries.list.loading"
      item-key="_id"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item._id }}</td>
          <td>{{ item.createdAt | formatDate }}</td>
          <td>{{ item.description }}</td>
          <td>{{ item.scopes.join(', ') }}</td>
          <td>
            <v-btn small icon @click="edit(item)">
              <v-icon color="primary">edit</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialog"
      width="700"
    >
      <v-card>
        <v-card-text>
          <v-row class="pt-5">
            <v-col cols="12">
              <v-form v-if="newAccessKey === null">
                <v-text-field
                  readonly
                  v-show="form.secretAccessKey"
                  label="Secret Access Key"
                  hint="Keep this value somewhere safe. When you press save, this value will be hashed."
                  persistent-hint
                  v-model="form.secretAccessKey"
                  ref="secretAccessKey"
                >
                  <template v-slot:append-outer>
                    <v-btn
                      @click="copySecretAccessKey"
                    >
                      <v-icon>content_copy</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
                <v-text-field
                  label="Description"
                  name="description"
                  v-model="form.description"
                />
                <v-combobox
                  v-model="form.scopes"
                  :items="scopes"
                  chips
                  clearable
                  label="Scopes"
                  multiple
                  solo
                >
                  <template v-slot:selection="{ attrs, item, select, selected }">
                    <v-chip
                      v-bind="attrs"
                      :input-value="selected"
                      close
                      @click="select"
                      @click:close="removeScope(item)"
                    >
                      <strong>{{ item }}</strong>
                    </v-chip>
                  </template>
                </v-combobox>
              </v-form>
              <div v-else>
                <div class="font-weight-black">
                  The following HTTP header can be used to authenticate API requests for the selected scopes.
                </div>
                <p class="text-subtitle-2 mt-5">Authorization Header:</p>
                <pre style="overflow: scroll;" class="mb-5">access-key {{ newAccessKeyId }}.{{ newAccessKey }}</pre>
                <p class="text-subtitle-2">HTTP Basic Auth:</p>
                <pre style="overflow: scroll;" class="mb-5">{{ newAccessKeyId }}:{{ newAccessKey }}</pre>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <template v-if="newAccessKey === null">
            <v-btn
              color="primary"
              text
              @click="save"
            >
              Save
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="dialog = false"
            >
              Cancel
            </v-btn>
          </template>
          <v-btn
            v-else
            color="primary"
            text
            @click="dialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import gql from 'graphql-tag'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'AccessKeyList',

  data: () => ({
    headers: [{
      text: 'Access Key ID',
      value: '_id',
      sortable: false
    }, {
      text: 'Issue date',
      value: 'createdAt',
      sortable: false
    }, {
      text: 'Description',
      value: 'description',
      sortable: false
    }, {
      text: 'Scopes',
      value: 'scopes',
      sortable: false
    }, {
      text: 'Actions',
      sortable: false
    }],
    pagination: {
      sortDesc: [false],
      page: 1,
      itemsPerPage: 10,
      sortBy: ['_id']
    },
    list: {
      count: 0,
      items: []
    },
    dialog: false,
    form: {
      description: null,
      scopes: [],
      secretAccessKey: null
    },
    scopeMany: [],
    newAccessKey: null,
    newAccessKeyId: null
  }),

  apollo: {
    list: {
      query: gql`query ListAccessKeys ($page: Int!, $perPage: Int!) {
        list: accessKeyManyPagination(page: $page, perPage: $perPage) {
          count
          items {
            _id
            createdAt
            description
            scopes
          }
        }
      }`,
      variables () {
        return {
          page: this.pagination.page,
          perPage: this.pagination.itemsPerPage
        }
      }
    },

    scopeMany: {
      query: gql`query ListScopes {
        scopeMany {
          value
        }
      }`
    }
  },

  computed: {
    scopes () {
      return this.scopeMany.map(o => o.value)
    }
  },

  methods: {
    add () {
      this.newAccessKey = null
      this.newAccessKeyId = null
      this.form = {
        description: null,
        scopes: [],
        secretAccessKey: uuidv4()
      }
      this.dialog = true
    },

    edit (item) {
      this.newAccessKey = null
      this.newAccessKeyId = null
      this.form = {
        _id: item._id,
        description: item.description,
        scopes: item.scopes
      }
      this.dialog = true
    },

    async save () {
      let mutation
      if (this.form._id) {
        mutation = {
          mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdAccessKeyInput!) {
            accessKeyUpdateById(_id: $id, record: $record){
              record {
                _id
                description
                scopes
              }
            }
          }`,
          variables: {
            id: this.form._id,
            record: {
              description: this.form.description,
              scopes: this.form.scopes
            }
          }
        }
      } else {
        mutation = {
          mutation: gql`mutation ($record: CreateOneAccessKeyInput!) {
            accessKeyCreateOne(record: $record){
              recordId
              error {
                message
              }
            }
          }`,
          variables: {
            record: this.form
          }
        }
      }

      try {
        const res = await this.$apollo.mutate(mutation)
        if (this.form.secretAccessKey) {
          const { recordId } = res.data.accessKeyCreateOne
          this.newAccessKey = window.btoa(this.form.secretAccessKey)
          this.newAccessKeyId = recordId
          this.$emit('info', 'Access Key created.')
        } else {
          this.dialog = false
          this.$emit('info', 'Access Key updated.')
        }
      } catch (e) {
        this.$emit('error', e)
        return
      }
      this.$apollo.queries.list.refresh()
    },

    removeScope (item) {
      this.form.scopes.splice(this.form.scopes.indexOf(item), 1)
    },

    copySecretAccessKey () {
      this.$refs.secretAccessKey.$el.querySelector('input').select()
      document.execCommand('copy')
      this.$emit('info', 'Secret access key copied to clipboard!')
    }
  }
}
</script>
